// src/pages/NumberHunt.jsx
import { useMemo, useState } from "react";
import { createNumberHunt } from "../api";
import jsPDF from "jspdf";

const AGE_OPTIONS = ["4-6", "7-9", "10-12"]; // keep consistent with backend schema

// -------------------------------
// PDF helpers
// -------------------------------
function addPdfHeader(doc, { title, subtitle }) {
  const margin = 48;
  const pageWidth = doc.internal.pageSize.getWidth();

  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text(title, margin, margin);

  if (subtitle) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(subtitle, margin, margin + 16);
  }

  // thin divider line
  doc.setDrawColor(220);
  doc.setLineWidth(0.5);
  doc.line(margin, margin + 26, pageWidth - margin, margin + 26);
}

function addPdfFooter(doc) {
  const margin = 48;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  const pageCount = doc.internal.getNumberOfPages();
  const pageNum = doc.internal.getCurrentPageInfo().pageNumber;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(120);
  doc.text(`Once-A-Hunt • Page ${pageNum} of ${pageCount}`, pageWidth - margin, pageHeight - 24, {
    align: "right",
  });
  doc.setTextColor(0);
}

function writeWrappedText(doc, { text, startY }) {
  const margin = 48;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const usableWidth = pageWidth - margin * 2;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);

  const lines = doc.splitTextToSize(text, usableWidth);
  const lineHeight = 14;

  let y = startY;

  for (let i = 0; i < lines.length; i++) {
    // If near bottom, add new page
    if (y > pageHeight - margin - 30) {
      addPdfFooter(doc);
      doc.addPage();
      y = margin + 44; // leave room for header on new pages
    }
    doc.text(lines[i], margin, y);
    y += lineHeight;
  }

  addPdfFooter(doc);
}

function downloadSinglePagePdf({ filename, headerTitle, headerSubtitle, bodyText }) {
  const doc = new jsPDF({ unit: "pt", format: "letter" });

  addPdfHeader(doc, { title: headerTitle, subtitle: headerSubtitle });

  writeWrappedText(doc, {
    text: bodyText,
    startY: 48 + 44, // margin + header area
  });

  doc.save(filename);
}

function downloadFullPackPdf({ filename, metaSubtitle, pages }) {
  const doc = new jsPDF({ unit: "pt", format: "letter" });

  // Page 1
  addPdfHeader(doc, { title: "Once-A-Hunt — Guardian Instructions", subtitle: metaSubtitle });
  writeWrappedText(doc, { text: pages.guardian_page, startY: 48 + 44 });

  // Page 2
  doc.addPage();
  addPdfHeader(doc, { title: "Once-A-Hunt — Child Hunt Page", subtitle: metaSubtitle });
  writeWrappedText(doc, { text: pages.child_page, startY: 48 + 44 });

  // Page 3
  doc.addPage();
  addPdfHeader(doc, { title: "Once-A-Hunt — Answer Key", subtitle: metaSubtitle });
  writeWrappedText(doc, { text: pages.answer_key_page, startY: 48 + 44 });

  doc.save(filename);
}

// -------------------------------
// Component
// -------------------------------
export default function NumberHunt() {
  // Form fields
  const [kidName, setKidName] = useState("");
  const [guardianRelation, setGuardianRelation] = useState("");
  const [ageRange, setAgeRange] = useState("7-9");

  const [locationA, setLocationA] = useState("");
  const [locationB, setLocationB] = useState("");
  const [locationC, setLocationC] = useState("");

  // Generated pages
  const [pages, setPages] = useState(null);

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const canGenerate = useMemo(() => {
    return (
      kidName.trim() &&
      guardianRelation.trim() &&
      locationA.trim() &&
      locationB.trim() &&
      locationC.trim()
    );
  }, [kidName, guardianRelation, locationA, locationB, locationC]);

  const metaSubtitle = useMemo(() => {
    const name = kidName.trim() || "your hunter";
    const rel = guardianRelation.trim() || "guardian";
    return `Hunter: ${name} • Guardian: ${rel} • Age group: ${ageRange}`;
  }, [kidName, guardianRelation, ageRange]);

  async function handleGenerate() {
    setError("");
    setLoading(true);
    setPages(null);

    try {
      const res = await createNumberHunt({
        kid_name: kidName.trim(),
        guardian_relation: guardianRelation.trim(),
        age_range: ageRange,
        location_a: locationA.trim(),
        location_b: locationB.trim(),
        location_c: locationC.trim(),
      });

      setPages(res);
    } catch (e) {
      console.error(e);

      // ✅ FIX: real template string, no eslint error
      setError(`Failed to generate scavenger hunt. ${e?.message || ""}`.trim());
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-10">
      <div className="bg-white rounded-2xl shadow-sm border border-brand-border p-6 sm:p-10">
        <h1 className="text-3xl sm:text-4xl font-extrabold text-brand-primary mb-2">
          Start your scavenger hunt
        </h1>

        <p className="text-brand-muted mb-6">
          Answer a few quick questions and we’ll generate a printable 3-page scavenger hunt:
          <span className="font-semibold text-brand-ink"> Guardian Instructions</span>, a{" "}
          <span className="font-semibold text-brand-ink">Child Hunt Page</span>, and an{" "}
          <span className="font-semibold text-brand-ink">Answer Key</span>.
        </p>

        {/* Quick setup note */}
        <div className="rounded-xl border border-brand-border bg-brand-cream p-4 mb-6">
          <div className="font-semibold text-brand-ink mb-1">Before you start</div>
          <ul className="list-disc pl-5 text-brand-muted space-y-1">
            <li>Choose any space you like: home, classroom, backyard, or even a park picnic table area.</li>
            <li>
              You’ll hide <span className="font-semibold text-brand-ink">three clue cards</span> in the locations you type
              below.
            </li>
            <li>
              You’ll also label <span className="font-semibold text-brand-ink">9 “final spots”</span> with numbers 1–9
              (drawers, cups, envelopes, sticky notes, etc.). The answer key will tell you which number gets the prize.
            </li>
          </ul>
        </div>

        {/* FORM */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className="block text-sm font-semibold text-brand-ink mb-1">Hunter’s name</label>
            <input
              value={kidName}
              onChange={(e) => setKidName(e.target.value)}
              placeholder="e.g., Aria"
              className="w-full border border-brand-border rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-brand-primary/30"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-brand-ink mb-1">Your relationship to the hunter</label>
            <input
              value={guardianRelation}
              onChange={(e) => setGuardianRelation(e.target.value)}
              placeholder="e.g., Mom, Dad, Aunt, Teacher"
              className="w-full border border-brand-border rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-brand-primary/30"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-brand-ink mb-1">Age group</label>
            <select
              value={ageRange}
              onChange={(e) => setAgeRange(e.target.value)}
              className="w-full border border-brand-border rounded-lg px-3 py-2 bg-white outline-none focus:ring-2 focus:ring-brand-primary/30"
            >
              {AGE_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
            <p className="text-xs text-brand-muted mt-1">(This version is tuned for 7–9, but you can pick other options now.)</p>
          </div>

          <div className="hidden md:block" />

          <div>
            <label className="block text-sm font-semibold text-brand-ink mb-1">
              Write for your hunter where the first clue is located
            </label>
            <input
              value={locationA}
              onChange={(e) => setLocationA(e.target.value)}
              placeholder="e.g., beside your piggy bank"
              className="w-full border border-brand-border rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-brand-primary/30"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-brand-ink mb-1">
              Write for your hunter where the second clue is located
            </label>
            <input
              value={locationB}
              onChange={(e) => setLocationB(e.target.value)}
              placeholder="e.g., inside the closet on the bottom shelf"
              className="w-full border border-brand-border rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-brand-primary/30"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-brand-ink mb-1">
              Write for your hunter where the third clue is located
            </label>
            <input
              value={locationC}
              onChange={(e) => setLocationC(e.target.value)}
              placeholder="e.g., under your pillow"
              className="w-full border border-brand-border rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-brand-primary/30"
            />
          </div>
        </div>

        {/* ACTIONS */}
        <div className="mt-6 flex flex-col gap-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleGenerate}
              disabled={!canGenerate || loading}
              className={[
                "rounded-lg px-5 py-3 font-semibold text-white shadow transition",
                !canGenerate || loading ? "bg-gray-400 cursor-not-allowed" : "bg-brand-primary hover:bg-brand-primaryDark",
              ].join(" ")}
            >
              {loading ? "Generating..." : "Generate my scavenger hunt"}
            </button>

            {pages && (
              <button
                onClick={() =>
                  downloadFullPackPdf({
                    filename: "onceahunt-full-pack.pdf",
                    metaSubtitle,
                    pages,
                  })
                }
                className="rounded-lg px-5 py-3 font-semibold text-white bg-brand-accent hover:bg-brand-accentDark shadow transition"
              >
                Download full pack (PDF)
              </button>
            )}
          </div>

          {pages && (
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() =>
                  downloadSinglePagePdf({
                    filename: "onceahunt-guardian-instructions.pdf",
                    headerTitle: "Once-A-Hunt — Guardian Instructions",
                    headerSubtitle: metaSubtitle,
                    bodyText: pages.guardian_page,
                  })
                }
                className="rounded-lg px-5 py-3 font-semibold text-white bg-brand-primary hover:bg-brand-primaryDark shadow transition"
              >
                Download guardian instructions (PDF)
              </button>

              <button
                onClick={() =>
                  downloadSinglePagePdf({
                    filename: "onceahunt-child-hunt.pdf",
                    headerTitle: "Once-A-Hunt — Child Hunt Page",
                    headerSubtitle: metaSubtitle,
                    bodyText: pages.child_page,
                  })
                }
                className="rounded-lg px-5 py-3 font-semibold text-white bg-brand-primary hover:bg-brand-primaryDark shadow transition"
              >
                Download child hunt (PDF)
              </button>

              <button
                onClick={() =>
                  downloadSinglePagePdf({
                    filename: "onceahunt-answer-key.pdf",
                    headerTitle: "Once-A-Hunt — Answer Key",
                    headerSubtitle: metaSubtitle,
                    bodyText: pages.answer_key_page,
                  })
                }
                className="rounded-lg px-5 py-3 font-semibold text-white bg-brand-primary hover:bg-brand-primaryDark shadow transition"
              >
                Download answer key (PDF)
              </button>
            </div>
          )}
        </div>

        {error && (
          <div className="mt-4 rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">{error}</div>
        )}

        {/* PREVIEW OUTPUT */}
        {pages && (
          <div className="mt-10 space-y-6">
            <h2 className="text-2xl font-extrabold text-brand-primary">Preview your printable pages</h2>

            <PageCard title="1) Guardian Instructions" text={pages.guardian_page} />
            <PageCard title="2) Child Hunt Page" text={pages.child_page} />
            <PageCard title="3) Answer Key" text={pages.answer_key_page} />
          </div>
        )}
      </div>
    </div>
  );
}

function PageCard({ title, text }) {
  return (
    <div className="rounded-2xl border border-brand-border bg-white shadow-sm overflow-hidden">
      <div className="px-5 py-3 bg-brand-cream border-b border-brand-border">
        <div className="font-bold text-brand-ink">{title}</div>
      </div>
      <pre className="p-5 whitespace-pre-wrap text-sm text-brand-ink leading-6">{text}</pre>
    </div>
  );
}
